import { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";

import HtmlText from "@/sections/common/HtmlText";
import ActionButton from "../atoms/buttons/ActionButton";
import icon, { IIconName } from "@/utils/icon.helper";

import { ITextButton } from "../atoms/buttons/types";

// ------------------------------------------------------------------------------------

export type IImageContentCard = {
  heading?: string;
  description?: ReactNode;
  image?: { src: string; alt: string };
  icon?: IIconName;
  button?: ITextButton;
  variant?: "secondary" | "white" | "light_primary";
};

export type IImageContentCardProps = HTMLAttributes<HTMLDivElement> &
  IImageContentCard & {};

const bgColor = {
  secondary: "bg-primary-100",
  white: "bg-white",
  light_primary: "bg-primary-50",
};

const ImageContentCard: FC<IImageContentCardProps> = (props) => {
  const {
    heading,
    description,
    variant = "secondary",
    image,
    icon: cardIcon,
    button,
    className,
    ...other
  } = props;

  return (
    <div
      className={clsx(
        // Card chrome — clean rounded card with subtle ring, lifts on
        // hover. Removed the flat bg + dated "translate content up to
        // reveal a hidden button" gimmick — buttons (when present) sit
        // permanently at the bottom now.
        "group relative flex h-full flex-col rounded-2xl p-5 ring-1 ring-black-100 transition-all duration-300 hover:-translate-y-1 hover:ring-primary-200 md:p-6 lg:p-7",
        bgColor[variant],
      )}
      {...other}
    >
      {(cardIcon || image) && (
        <div className="mb-5 flex h-12 items-center md:h-14 lg:mb-6">
          {cardIcon && (
            <Image
              src={icon(cardIcon)}
              className="h-10 w-10 lg:h-12 lg:w-12"
              alt={cardIcon}
              width={48}
              height={48}
            />
          )}
          {image && !cardIcon && (
            <Image
              src={image.src}
              alt={image.alt}
              width={168}
              height={46}
              className="h-10 w-auto lg:h-12"
            />
          )}
        </div>
      )}

      {(heading || description) && (
        <div className="flex flex-1 flex-col">
          {heading && (
            // Canonical card-heading scale matching IndustryCard /
            // SolutionCard — Bricolage Grotesque + fluid clamp.
            <h3 className="mb-3 font-bricolage-grotesque text-[clamp(1.125rem,0.75vw+0.875rem,1.375rem)] font-medium !leading-tight text-black-900 lg:mb-4">
              {heading}
            </h3>
          )}
          {description && (
            // Canonical body clamp + responsive weight. line-clamp
            // removed — descriptions read end-to-end per the sitewide
            // "don't truncate" guidance.
            <HtmlText className="prose-p:!text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] prose-p:!font-light prose-p:!leading-relaxed prose-p:!text-black-700 lg:prose-p:!font-extralight">
              {description}
            </HtmlText>
          )}
        </div>
      )}

      {button && (
        // Canonical card-action button — ActionButton (blue pill) for
        // per-card nav/conversion. Ignores the CMS variant + radius
        // because the legacy values (yellow + none) don't map to our
        // theme; the component-level treatment is intentionally uniform
        // across card grids so the eye reads them as a coherent set.
        <div className="mt-5 lg:mt-6">
          <ActionButton
            id={button.id}
            href={button.link}
            target={button.target}
            variant="primary"
            size="sm"
          >
            {button.text}
          </ActionButton>
        </div>
      )}
    </div>
  );
};

export default ImageContentCard;
